import { Button, Chart, Dialog, Field, Input, Select } from '@cloudflare/kumo';
import * as echarts from 'echarts';
import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react';
import { ICONS } from '../components/ui/icons';
import { PageLayout } from '../components/ui/PageLayout';
import { useAuthenticatedFetch } from '../hooks/useAuthenticatedFetch';
import { useAppToast } from '../hooks/useAppToast';
import { useTenant } from '../contexts/TenantContext';
import { PageSkeleton, SkeletonCards } from '../components/ui/Skeleton';

/**
 * Painéis personalizados: cada organização monta os seus com widgets (indicador, linha,
 * barras ou tabela) sobre dois conjuntos de dados: eventos de produto (Métricas) e uso de
 * IA (Observabilidade). O período vale para o painel inteiro.
 */

type WType = 'kpi' | 'line' | 'bar' | 'table';
interface Widget { id: string; type: WType; title: string; source: string; width: 'half' | 'full' }
interface Dash { id: string; name: string; widgets: Widget[] }
type ChartOptions = ComponentProps<typeof Chart>['options'];

const SOURCES: Array<{ value: string; label: string; group: 'Eventos' | 'IA' }> = [
  { value: 'events.contacts', label: 'Contatos novos', group: 'Eventos' },
  { value: 'events.conversations', label: 'Conversas iniciadas', group: 'Eventos' },
  { value: 'events.messages', label: 'Mensagens recebidas', group: 'Eventos' },
  { value: 'events.handoffs', label: 'Passadas para a equipe', group: 'Eventos' },
  { value: 'events.deals', label: 'Negócios criados', group: 'Eventos' },
  { value: 'events.won', label: 'Negócios ganhos', group: 'Eventos' },
  { value: 'events.revenue', label: 'Receita ganha', group: 'Eventos' },
  { value: 'ai.turns', label: 'Turnos de IA', group: 'IA' },
  { value: 'ai.errors', label: 'Erros de IA', group: 'IA' },
  { value: 'ai.tokens', label: 'Tokens', group: 'IA' },
  { value: 'ai.cost', label: 'Custo estimado', group: 'IA' },
  { value: 'ai.latency', label: 'Latência p95', group: 'IA' },
  { value: 'ai.byAgent', label: 'Uso por agente', group: 'IA' },
  { value: 'ai.byModel', label: 'Uso por modelo', group: 'IA' },
];
const TYPES: Array<{ value: WType; label: string }> = [
  { value: 'kpi', label: 'Indicador' }, { value: 'line', label: 'Linha' }, { value: 'bar', label: 'Barras' }, { value: 'table', label: 'Tabela' },
];
const PERIODS = [{ value: '7', label: 'Últimos 7 dias' }, { value: '30', label: 'Últimos 30 dias' }, { value: '90', label: 'Últimos 90 dias' }];
const EVENT_OF: Record<string, string> = {
  contacts: 'contact.created', conversations: 'conversation.started', messages: 'message.received',
  handoffs: 'conversation.handoff', deals: 'deal.created', won: 'deal.won', revenue: 'deal.won',
};

interface Overview { kpis: Array<{ event: string; count: number }>; revenueWon: { amount: number }; daily: Array<Record<string, number | string>> }
interface AiDay { day: string; turns: number; tokensIn: number; tokensOut: number; p95Ms: number; errors: number; costMicroUsd: number }
interface AiKeyed extends AiDay { key: string }
interface Ai { usage: { available: true; days: AiDay[]; totals: AiDay; byAgent: AiKeyed[]; byModel: AiKeyed[] } | { available: false; message: string }; agents: Array<{ id: string; name: string }> }

const num = (v: number) => v.toLocaleString('pt-BR');
const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const usd = (micro: number) => (micro / 1_000_000).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });
const dayLabel = (iso: string) => new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
const uid = () => Math.random().toString(36).slice(2, 10);

interface Series { labels: string[]; values: number[]; total: string; fmt: (v: number) => string }
interface Rows { head: string[]; rows: string[][] }

function seriesOf(source: string, ev: Overview | null, ai: Ai | null): Series | null {
  const [kind, key] = source.split('.');
  if (kind === 'events') {
    if (!ev) return null;
    const e = EVENT_OF[key];
    const values = ev.daily.map((d) => Number(d[e] ?? 0));
    const labels = ev.daily.map((d) => dayLabel(String(d.day)));
    if (key === 'revenue') return { labels, values, total: brl(ev.revenueWon.amount), fmt: num };
    const total = ev.kpis.find((k) => k.event === e)?.count ?? values.reduce((a, b) => a + b, 0);
    return { labels, values, total: num(total), fmt: num };
  }
  const u = ai?.usage;
  if (!u || !u.available) return null;
  const days = u.days; const labels = days.map((d) => dayLabel(d.day)); const t = u.totals;
  switch (key) {
    case 'turns': return { labels, values: days.map((d) => d.turns), total: num(t.turns), fmt: num };
    case 'errors': return { labels, values: days.map((d) => d.errors), total: num(t.errors), fmt: num };
    case 'tokens': return { labels, values: days.map((d) => d.tokensIn + d.tokensOut), total: num(t.tokensIn + t.tokensOut), fmt: num };
    case 'cost': return { labels, values: days.map((d) => d.costMicroUsd), total: usd(t.costMicroUsd), fmt: usd };
    case 'latency': return { labels, values: days.map((d) => d.p95Ms), total: `${(t.p95Ms / 1000).toFixed(1)} s`, fmt: (v) => `${(v / 1000).toFixed(1)} s` };
    default: return null;
  }
}

function rowsOf(source: string, ev: Overview | null, ai: Ai | null): Rows | null {
  const [kind, key] = source.split('.');
  if (kind === 'ai' && (key === 'byAgent' || key === 'byModel')) {
    const u = ai?.usage; if (!u || !u.available) return null;
    const names = new Map((ai?.agents ?? []).map((a) => [a.id, a.name]));
    const list = key === 'byAgent' ? u.byAgent : u.byModel;
    return {
      head: [key === 'byAgent' ? 'Agente' : 'Modelo', 'Turnos', 'Tokens', 'Erros', 'Custo'],
      rows: list.map((r) => [key === 'byAgent' ? (names.get(r.key) ?? r.key) : r.key, num(r.turns), num(r.tokensIn + r.tokensOut), num(r.errors), usd(r.costMicroUsd)]),
    };
  }
  const s = seriesOf(source, ev, ai); if (!s) return null;
  const label = SOURCES.find((x) => x.value === source)?.label ?? 'Valor';
  return { head: ['Dia', label], rows: s.labels.map((l, i) => [l, s.fmt(s.values[i])]).reverse() };
}

function WidgetView({ w, ev, ai, ready, failed }: { w: Widget; ev: Overview | null; ai: Ai | null; ready: boolean; failed: boolean }) {
  const label = SOURCES.find((s) => s.value === w.source)?.label ?? w.source;
  const isAi = w.source.startsWith('ai.');
  const aiOff = isAi && ai?.usage && !ai.usage.available ? ai.usage.message : null;
  const noEvents = !isAi && failed;
  const s = seriesOf(w.source, ev, ai);
  if (aiOff || noEvents) return <div className="text-sm text-kumo-inactive">{aiOff ?? 'Indisponível: o plano não inclui relatórios avançados.'}</div>;
  if (!ready) return <PageSkeleton label="Carregando painéis" />;
  if (w.type === 'kpi') return s ? <div><div className="text-3xl font-semibold" data-testid="widget-valor">{s.total}</div><div className="text-xs text-kumo-subtle">{label}</div></div> : <div className="text-sm text-kumo-inactive">Sem dados.</div>;
  if (w.type === 'table') {
    const r = rowsOf(w.source, ev, ai);
    if (!r || !r.rows.length) return <div className="text-sm text-kumo-inactive">Sem dados no período.</div>;
    return (
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-sm"><thead><tr>{r.head.map((h) => <th key={h} className="py-1 pr-3 text-left font-medium text-kumo-subtle">{h}</th>)}</tr></thead>
          <tbody>{r.rows.map((row, i) => <tr key={i} className="border-t border-kumo-hairline">{row.map((c, j) => <td key={j} className="py-1 pr-3">{c}</td>)}</tr>)}</tbody></table>
      </div>
    );
  }
  let options: ChartOptions | null = null;
  if (w.source === 'ai.byAgent' || w.source === 'ai.byModel') {
    const u = ai?.usage;
    if (u && u.available) {
      const list = w.source === 'ai.byAgent' ? u.byAgent : u.byModel;
      const names = new Map((ai?.agents ?? []).map((a) => [a.id, a.name]));
      options = { tooltip: { trigger: 'axis' }, grid: { left: 44, right: 16, top: 16, bottom: 40 }, xAxis: { type: 'category', data: list.map((r) => names.get(r.key) ?? r.key) }, yAxis: { type: 'value', minInterval: 1 }, series: [{ name: 'Turnos', type: 'bar', data: list.map((r) => r.turns) }] };
    }
  } else if (s && s.values.length) {
    options = {
      tooltip: { trigger: 'axis', valueFormatter: (v: unknown) => s.fmt(Number(v)) },
      grid: { left: 52, right: 16, top: 16, bottom: 32 },
      xAxis: { type: 'category', data: s.labels }, yAxis: { type: 'value', minInterval: 1 },
      series: [{ name: label, type: w.type === 'line' ? 'line' : 'bar', smooth: w.type === 'line', data: s.values }],
    };
  }
  return options ? <Chart echarts={echarts} options={options} height={220} /> : <div className="text-sm text-kumo-inactive">Sem dados no período.</div>;
}

export default function PaineisPage() {
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const [list, setList] = useState<Dash[] | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Dash | null>(null);
  const [naming, setNaming] = useState<string | null>(null);
  const [adding, setAdding] = useState<Widget | null>(null);
  const [ev, setEv] = useState<Overview | null>(null);
  const [ai, setAi] = useState<Ai | null>(null);
  const [evFailed, setEvFailed] = useState(false);
  const [ready, setReady] = useState(false);

  const load = useCallback(async (select?: string) => {
    const r = await fetchApi('/api/dashboards');
    const j = r.ok ? await r.json() : { data: [] };
    const rows: Dash[] = j.data ?? [];
    setList(rows);
    setCurrent((c) => select ?? (rows.some((d) => d.id === c) ? c : rows[0]?.id ?? null));
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const dash = list?.find((d) => d.id === current) ?? null;
  const shown = editing && draft ? draft : dash;
  const sources = useMemo(() => new Set((shown?.widgets ?? []).map((w) => w.source.split('.')[0])), [shown]);

  useEffect(() => {
    if (!shown) return;
    let alive = true; setReady(false); setEvFailed(false);
    const end = new Date(); const start = new Date(Date.now() - days * 86_400_000);
    const jobs: Array<Promise<void>> = [];
    if (sources.has('events')) {
      const q = new URLSearchParams({ startDate: start.toISOString(), endDate: end.toISOString() });
      jobs.push(fetchApi(`/api/analytics/events/overview?${q}`).then(async (r) => { if (!r.ok) throw new Error('x'); const j = await r.json(); if (alive) setEv(j.data ?? j); }).catch(() => { if (alive) { setEv(null); setEvFailed(true); } }));
    }
    if (sources.has('ai')) {
      jobs.push(fetchApi(`/api/ai/observability?days=${days}`).then(async (r) => { if (!r.ok) throw new Error('x'); const j = await r.json(); if (alive) setAi(j.data ?? j); }).catch(() => { if (alive) setAi(null); }));
    }
    Promise.all(jobs).then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [days, sources, shown?.id, fetchApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (d: Dash) => {
    const r = await fetchApi(`/api/dashboards/${d.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: d.name, widgets: d.widgets }) });
    if (!r.ok) { toast.error('Não foi possível salvar o painel'); return false; }
    await load(d.id); toast.success('Painel salvo'); return true;
  };
  const create = async (name: string) => {
    const r = await fetchApi('/api/dashboards', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name, widgets: [] }) });
    if (!r.ok) { toast.error('Não foi possível criar o painel'); return; }
    const j = await r.json(); await load(j.data.id); setDraft({ ...j.data, widgets: [] }); setEditing(true);
  };
  const remove = async () => {
    if (!dash || !window.confirm(`Excluir o painel "${dash.name}"?`)) return;
    const r = await fetchApi(`/api/dashboards/${dash.id}`, { method: 'DELETE' });
    if (!r.ok) { toast.error('Não foi possível excluir'); return; }
    setEditing(false); setDraft(null); setCurrent(null); await load(); toast.success('Painel excluído');
  };
  const move = (i: number, to: number) => setDraft((d) => { if (!d || to < 0 || to >= d.widgets.length) return d; const w = [...d.widgets]; [w[i], w[to]] = [w[to], w[i]]; return { ...d, widgets: w }; });

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={String(days)} items={PERIODS} onValueChange={(v: string | null) => { if (v) setDays(Number(v)); }} aria-label="Período" />
      {canEdit && !editing && dash && <Button variant="secondary" icon={ICONS.Edit} onClick={() => { setDraft(structuredClone(dash)); setEditing(true); }}>Editar</Button>}
      {canEdit && editing && <>
        <Button variant="secondary" onClick={() => { setEditing(false); setDraft(null); }}>Cancelar</Button>
        <Button variant="primary" onClick={async () => { if (draft && await save(draft)) { setEditing(false); setDraft(null); } }}>Salvar</Button>
      </>}
      {canEdit && !editing && <Button variant="primary" icon={ICONS.Plus} onClick={() => setNaming('')}>Novo painel</Button>}
    </div>
  );

  return (
    <PageLayout title="Painéis" description="Monte painéis com os números que importam para a sua equipe: atendimento, vendas e uso de IA." actions={actions}>
      <div className="flex flex-col gap-4" data-testid="paineis">
        {list && list.length > 0 && (
          <div className="flex flex-wrap gap-2" role="tablist">
            {list.map((d) => (
              <button key={d.id} type="button" role="tab" aria-selected={d.id === current} disabled={editing}
                className={`rounded-full border px-3 py-1 text-sm ${d.id === current ? 'border-kumo-brand bg-kumo-tint font-medium' : 'border-kumo-hairline text-kumo-subtle'}`}
                onClick={() => setCurrent(d.id)}>{d.name}</button>
            ))}
          </div>
        )}
        {list && list.length === 0 && (
          <div className="rounded-xl border border-dashed border-kumo-hairline p-10 text-center">
            <div className="text-lg font-semibold">Nenhum painel ainda</div>
            <div className="mb-4 text-kumo-subtle">Crie um painel e adicione indicadores, gráficos e tabelas.</div>
            {canEdit ? <Button variant="primary" icon={ICONS.Plus} onClick={() => setNaming('')}>Criar o primeiro painel</Button> : <div className="text-sm text-kumo-inactive">Peça a quem administra a organização para criar um painel.</div>}
          </div>
        )}
        {shown && editing && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-60 flex-1"><Field label="Nome do painel"><Input value={shown.name} onChange={(e) => setDraft((d) => (d ? { ...d, name: e.target.value } : d))} /></Field></div>
            <Button variant="secondary" icon={ICONS.Plus} onClick={() => setAdding({ id: uid(), type: 'kpi', title: '', source: 'events.conversations', width: 'half' })} disabled={shown.widgets.length >= 24}>Adicionar widget</Button>
            <Button variant="secondary-destructive" icon={ICONS.Trash} onClick={remove}>Excluir painel</Button>
          </div>
        )}
        {shown && shown.widgets.length === 0 && <div className="text-kumo-subtle">{editing ? 'Adicione o primeiro widget.' : 'Este painel está vazio. Use Editar para adicionar widgets.'}</div>}
        {shown && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {shown.widgets.map((w, i) => (
              <div key={w.id} className={`rounded-xl border border-kumo-hairline bg-kumo-base p-4 ${w.width === 'full' ? 'md:col-span-2' : ''}`} data-testid="widget">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="font-semibold">{w.title}</div>
                  {editing && (
                    <div className="flex gap-1">
                      <Button variant="ghost" shape="square" aria-label="Mover para antes" onClick={() => move(i, i - 1)} disabled={i === 0}>↑</Button>
                      <Button variant="ghost" shape="square" aria-label="Mover para depois" onClick={() => move(i, i + 1)} disabled={i === shown.widgets.length - 1}>↓</Button>
                      <Button variant="ghost" shape="square" aria-label="Editar widget" icon={ICONS.Edit} onClick={() => setAdding({ ...w })} />
                      <Button variant="ghost" shape="square" aria-label="Remover widget" icon={ICONS.Trash} onClick={() => setDraft((d) => (d ? { ...d, widgets: d.widgets.filter((x) => x.id !== w.id) } : d))} />
                    </div>
                  )}
                </div>
                <WidgetView w={w} ev={ev} ai={ai} ready={ready} failed={evFailed} />
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog.Root open={naming !== null} onOpenChange={(o: boolean) => { if (!o) setNaming(null); }}>
        <Dialog size="sm" className="p-6">
          <Dialog.Title className="mb-4 text-lg font-semibold">Novo painel</Dialog.Title>
          <Field label="Nome"><Input value={naming ?? ''} onChange={(e) => setNaming(e.target.value)} placeholder="Ex.: Atendimento da semana" data-testid="painel-nome" /></Field>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setNaming(null)}>Cancelar</Button>
            <Button variant="primary" disabled={!naming?.trim()} onClick={() => { const n = naming!.trim(); setNaming(null); create(n); }}>Criar</Button>
          </div>
        </Dialog>
      </Dialog.Root>

      <Dialog.Root open={adding !== null} onOpenChange={(o: boolean) => { if (!o) setAdding(null); }}>
        <Dialog size="base" className="p-6">
          <Dialog.Title className="mb-4 text-lg font-semibold">Widget</Dialog.Title>
          {adding && (
            <div className="flex flex-col gap-3">
              <Field label="Título"><Input value={adding.title} onChange={(e) => setAdding({ ...adding, title: e.target.value })} data-testid="widget-titulo" /></Field>
              <Field label="Dados"><Select value={adding.source} items={SOURCES.map((s) => ({ value: s.value, label: `${s.group} · ${s.label}` }))} onValueChange={(v: string | null) => { if (v) setAdding({ ...adding, source: v, type: v.endsWith('byAgent') || v.endsWith('byModel') ? (adding.type === 'kpi' ? 'bar' : adding.type) : adding.type }); }} aria-label="Dados" /></Field>
              <Field label="Visual"><Select value={adding.type} items={TYPES.filter((t) => !(t.value === 'kpi' && /by(Agent|Model)$/.test(adding.source)))} onValueChange={(v: WType | null) => { if (v) setAdding({ ...adding, type: v }); }} aria-label="Visual" /></Field>
              <Field label="Largura"><Select value={adding.width} items={[{ value: 'half', label: 'Metade' }, { value: 'full', label: 'Inteira' }]} onValueChange={(v: string | null) => { if (v === 'half' || v === 'full') setAdding({ ...adding, width: v }); }} aria-label="Largura" /></Field>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setAdding(null)}>Cancelar</Button>
            <Button variant="primary" disabled={!adding?.title.trim()} onClick={() => {
              if (!adding) return; const w = { ...adding, title: adding.title.trim() };
              setDraft((d) => (d ? { ...d, widgets: d.widgets.some((x) => x.id === w.id) ? d.widgets.map((x) => (x.id === w.id ? w : x)) : [...d.widgets, w] } : d));
              setAdding(null);
            }}>Aplicar</Button>
          </div>
        </Dialog>
      </Dialog.Root>
      {list === null && <SkeletonCards count={4} label="Carregando painéis" />}
    </PageLayout>
  );
}
